import React, { useMemo } from 'react';
import { X, MapPin, Package, Store } from 'lucide-react';

function PickupsList({ pickups, onClose, onPickupClick }) {
  
  // Sort pickups by sequence (if exists), then by id
  const sortedPickups = useMemo(() => {
    return [...pickups].sort((a, b) => {
      if (a.sequence && b.sequence) {
        return a.sequence - b.sequence;
      }
      if (a.sequence) return -1;
      if (b.sequence) return 1;
      return a.id - b.id;
    });
  }, [pickups]);

  function getStatusBadge(status) {
    switch (status) {
      case 'pending':
        return 'bg-orange-100 text-orange-700 text-xs px-2 py-0.5 rounded-full font-semibold';
      case 'encamino':
        return 'bg-blue-100 text-blue-700 text-xs px-2 py-0.5 rounded-full font-semibold';
      case 'recogido':
        return 'bg-green-100 text-green-700 text-xs px-2 py-0.5 rounded-full font-semibold';
      case 'cancelado':
        return 'bg-red-100 text-red-700 text-xs px-2 py-0.5 rounded-full font-semibold';
      default:
        return 'bg-slate-100 text-slate-700 text-xs px-2 py-0.5 rounded-full font-semibold';
    }
  }

  function getStatusLabel(status) {
    const labels = {
      pending: 'Pendiente',
      encamino: 'En Camino',
      recogido: 'Recogido',
      cancelado: 'Cancelado',
    };
    return labels[status] || status;
  }

  function PickupItem({ pickup }) {
    return (
      <button
        onClick={() => onPickupClick(pickup)}
        className="w-full bg-white rounded-lg p-3 shadow-sm border border-slate-200 hover:shadow-md hover:border-[#67A43C]/30 transition-all active:scale-[0.98]"
      >
        <div className="flex items-start gap-3">
          {/* Sequence Indicator */}
          <div className="flex-shrink-0">
            {pickup.sequence ? (
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#67A43C] to-[#E6B43C] flex items-center justify-center shadow-sm">
                <span className="text-white font-bold text-sm font-montserrat">
                  {pickup.sequence}
                </span>
              </div>
            ) : (
              <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center">
                <Package className="w-5 h-5 text-slate-500" />
              </div>
            )}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0 text-left">
            <div className="flex items-start justify-between gap-2 mb-1">
              <h3 className="font-semibold text-sm text-slate-900 font-montserrat truncate">
                {pickup.customer_name || 'Sin nombre'}
              </h3>
              <span className={getStatusBadge(pickup.status)}>
                {getStatusLabel(pickup.status)}
              </span>
            </div>
            
            {pickup.store_name && (
              <p className="text-xs text-slate-700 font-montserrat mb-1 flex items-center gap-1">
                <Store className="w-3 h-3 inline" />
                {pickup.store_name}
              </p>
            )}
            
            {pickup.address && (
              <p className="text-xs text-slate-600 font-montserrat truncate mb-1">
                <MapPin className="w-3 h-3 inline mr-1" />
                {pickup.address}
              </p>
            )}
            
            {pickup.package_count && (
              <p className="text-xs text-slate-500 font-montserrat">
                <Package className="w-3 h-3 inline mr-1" />
                {pickup.package_count} paquete{pickup.package_count !== 1 ? 's' : ''}
              </p>
            )}
          </div>
        </div>
      </button>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[2000] p-3 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <Package className="w-5 h-5 text-[#67A43C]" />
            <div>
              <h2 className="text-lg font-extrabold text-[#67A43C] font-montserrat">
                Lista de Recojos
              </h2>
              <p className="text-xs text-slate-600 font-montserrat">
                Total: {pickups.length}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-4">
          {pickups.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Package className="w-16 h-16 text-slate-300 mb-3" />
              <p className="text-slate-600 font-montserrat font-semibold">
                No hay recojos disponibles
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {sortedPickups.map((pickup) => (
                <PickupItem key={pickup.id} pickup={pickup} />
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-slate-200 p-4">
          <p className="text-xs text-slate-600 font-montserrat text-center">
            Toca un recojo para ver más detalles o asignar secuencia
          </p>
        </div>
      </div>
    </div>
  );
}

export default PickupsList;
